import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import { DfcVillageHeaderState } from '../../hooks/useDfcVillageHeader';
import VillageLogo, { useHasDarkHeaderLogo } from './VillageLogo';
import ThemeSwitch from './ThemeSwitch';
import LanguageSwitch from './LanguageSwitch';
import {
  ActionGroup,
  CartBadge,
  HeaderBar,
  HeaderRow,
  LogoPlate,
  NavItem,
  NavList,
  NavRule,
  PillButton,
  RoundButton,
} from './style';

interface DesktopHeaderProps {
  state: DfcVillageHeaderState;
}

/**
 * 960px and up: lockup, the company nav with a green rule under the active item, then the
 * day / night switch and sign in.
 */
const DesktopHeader: React.FC<DesktopHeaderProps> = ({ state }) => {
  const {
    surface,
    handleLogoClick,
    navItems,
    handleNavItemClick,
    showCartButton,
    cartItemsCount,
    handleNavigateToCart,
    showAccount,
    isSignedIn,
    enableQRCode,
    handleAccountClick,
    handleSignIn,
  } = state;
  const copy = useFitnessVillageCopy().header;
  const hasDarkLogo = useHasDarkHeaderLogo();

  return (
    <HeaderBar surface={surface} position="sticky" elevation={0}>
      <HeaderRow>
        <LogoPlate surface={surface} bare={hasDarkLogo} onClick={handleLogoClick} aria-label={copy.home}>
          <VillageLogo />
        </LogoPlate>

        <NavList component="nav" aria-label={copy.mainNav}>
          {navItems.map(item => (
            <NavItem
              key={item.to}
              surface={surface}
              active={item.active}
              aria-current={item.active ? 'page' : undefined}
              onClick={() => handleNavItemClick(item.to)}
            >
              {item.name}
              <NavRule active={item.active} />
            </NavItem>
          ))}
        </NavList>

        <ActionGroup>
          <LanguageSwitch surface={surface} variant="full" />
          <ThemeSwitch surface={surface} variant="pill" />

          {showCartButton && (
            <RoundButton
              surface={surface}
              onClick={handleNavigateToCart}
              aria-label={copy.cart}
              data-testid="cart-button"
            >
              <CartBadge badgeContent={cartItemsCount}>
                <ShoppingCartOutlinedIcon />
              </CartBadge>
            </RoundButton>
          )}

          {showAccount &&
            (isSignedIn ? (
              <RoundButton
                surface={surface}
                onClick={handleAccountClick}
                aria-label={enableQRCode ? copy.quickCheckIn : copy.account}
                data-testid="account-button"
              >
                {enableQRCode ? <QrCode2Icon /> : <PersonIcon />}
              </RoundButton>
            ) : (
              <PillButton surface={surface} variant="solid" size="md" onClick={handleSignIn}>
                {copy.signIn}
              </PillButton>
            ))}
        </ActionGroup>
      </HeaderRow>
    </HeaderBar>
  );
};

export default DesktopHeader;
